import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { ConfigService } from '@shared-lib/shared-services/config.service';

/** Dropdown field keyword sent in the ReportDropDown request payload. */
export type DropdownField =
  | 'marketing_configuration'
  | 'technical_configuration'
  | 'fuel_rating_hp';

/** The three preselected dropdown values that scope a history request. */
export interface HistorySelection {
  marketing_configuration: string | null;
  technical_configuration: string | null;
  fuel_rating_hp: string | null;
}

/**
 * One record in the pricing-action history, normalized from the platform's
 * entity `data` object. The API's `new_price` / `new_effective_date`
 * are mapped onto `price` / `effective_date` so the existing chart and table can
 * consume the records unchanged.
 */
export interface PricingActionRecord {
  marketing_configuration: string;
  technical_configuration: string;
  fuel_rating_hp: string;
  price: number;
  effective_date: string;
  pricing_action: string;
  change_reason: string;
  notes: string;
}

/** Data needed to render the filters, history chart, and Deep Dive table. */
export interface PricingActionInitialData {
  marketing: string[];
  technical: string[];
  fuel: string[];
  records: PricingActionRecord[];
}

@Injectable({ providedIn: 'root' })
export class DeepDiveService {
  private config = inject(ConfigService);

  /** Platform endpoint that resolves both the dropdown and the history functions. */
  private static readonly PLATFORM_URL = '/spriced/platform';

  /** Loads all filter options and report rows required by the initial screen render. */
  getInitialData(): Observable<PricingActionInitialData> {
    const payload = {
      requestData: { initial_load: true },
      responseList: [],
      entity: 'default_entity',
      workflowId: 'default_workflow',
      endpoints: [
        { functionName: 'RERPricingInsightsCascadingFiltersService', version: '1.0' },
      ],
      version: '1.0',
    };

    return this.config
      .getApi(DeepDiveService.PLATFORM_URL, {}, { rawPayload: payload }, 'post')
      .pipe(
        map((res) => ({
          marketing: DeepDiveService.extractOptions(res, 'marketing_configuration'),
          technical: DeepDiveService.extractOptions(res, 'technical_configuration'),
          fuel: DeepDiveService.extractOptions(res, 'fuel_rating_hp'),
          records: DeepDiveService.extractRecords(res),
        })),
        catchError(() =>
          of<PricingActionInitialData>({
            marketing: [],
            technical: [],
            fuel: [],
            records: [],
          }),
        ),
      );
  }

  /**
   * Loads the pricing-action history for the preselected dropdown values via the
   * `CuREREnginePricingReport` platform function. The three configs are passed in
   * `requestData`, and entity rows nested below `responseList` are normalized to
   * `PricingActionRecord`s the chart and table consume.
   */
  getHistory(selection: HistorySelection): Observable<PricingActionRecord[]> {
    const requestData: Record<string, string> = {};
    for (const [key, value] of Object.entries(selection)) {
      if (value != null && value !== '') {
        requestData[key] = value;
      }
    }
    const payload = {
      requestData,
      responseList: [],
      entity: 'default_entity',
      workflowId: 'default_workflow',
      endpoints: [{ functionName: 'RERPricingActionHistoryService', version: '1.0' }],
      version: '1.0',
    };

    return this.config
      .getApi(DeepDiveService.PLATFORM_URL, {}, { rawPayload: payload }, 'post')
      .pipe(
        map((res) => DeepDiveService.extractRecords(res)),
        catchError(() => of<PricingActionRecord[]>([])),
      );
  }

  /**
   * Finds history records anywhere below the response envelope. This supports
   * both the former history collection and the new entity wrapper
   * (`responseList[].cu_rer_engine_pricing[].data`).
   */
  private static extractRecords(res: any): PricingActionRecord[] {
    const records: PricingActionRecord[] = [];
    DeepDiveService.visitResponseObjects(res, (data) => {
      if (DeepDiveService.isHistoryRecord(data)) {
        records.push({
          marketing_configuration: data['marketing_configuration'] ?? '',
          technical_configuration: data['technical_configuration'] ?? '',
          fuel_rating_hp: data['fuel_rating_hp'] ?? '',
          price: Number(data['new_price'] ?? data['price'] ?? data['current_price'] ?? 0),
          effective_date:
            data['new_effective_date'] ??
            data['effective_date'] ??
            data['current_effective_date'] ??
            '',
          pricing_action: data['pricing_action'] ?? '',
          change_reason: data['change_reason'] ?? '',
          notes: data['notes'] ?? '',
        });
      }
    });
    return records;
  }

  private static isHistoryRecord(value: Record<string, any>): boolean {
    return [
      'new_price',
      'price',
      'new_effective_date',
      'effective_date',
      'pricing_action',
      'change_reason',
      'notes',
    ].some((key) => Object.prototype.hasOwnProperty.call(value, key));
  }

  /**
   * Fetches the distinct option values for a single dropdown via the
   * `ReportDropDown` platform function. The requested column is passed as the
   * key inside `requestData`, so the same call shape serves all three
   * dropdowns — only the `field` changes (marketing_configuration /
   * technical_configuration / fuel_rating_hp).
   */
  getDropdownOptions(field: DropdownField): Observable<string[]> {
    const payload = {
      requestData: { [field]: null },
      responseList: [],
      entity: 'engine_pricing',
      workflowId: 'UI',
      endpoints: [{ functionName: 'ReportDropDown', version: '1.0' }],
      communicationType: 'rest',
      version: '1.0',
    };

    return this.config
      .getApi(DeepDiveService.PLATFORM_URL, {}, { rawPayload: payload }, 'post')
      .pipe(
        map((res) => DeepDiveService.extractOptions(res, field)),
        catchError(() => of<string[]>([])),
      );
  }

  /**
   * Pulls distinct scalar values for the requested field from the response.
   * Values may use the former `responseList[0][field]` shape or the new entity
   * shape: `responseList[].cu_rer_engine_pricing[].data[field]`.
   */
  private static extractOptions(res: any, field: DropdownField): string[] {
    const values: string[] = [];
    DeepDiveService.visitResponseObjects(res, (item) => {
      const value = item[field];
      if (Array.isArray(value)) {
        value.forEach((option) => {
          if (option != null && option !== '' && typeof option !== 'object') {
            values.push(String(option));
          }
        });
      } else if (value != null && value !== '' && typeof value !== 'object') {
        values.push(String(value));
      }
    });
    return Array.from(new Set(values)).filter((value) => value !== 'All');
  }

  /** Walks arrays and objects in an API envelope and exposes every object row. */
  private static visitResponseObjects(
    value: any,
    visitor: (value: Record<string, any>) => void,
  ): void {
    if (Array.isArray(value)) {
      value.forEach((item) => DeepDiveService.visitResponseObjects(item, visitor));
      return;
    }
    if (value && typeof value === 'object') {
      visitor(value);
      Object.values(value).forEach((item) =>
        DeepDiveService.visitResponseObjects(item, visitor),
      );
    }
  }
}
